/**
 * ANN Governance Frameworks - React PWA Frontend
 * Beautiful, responsive UI for all 10 governance modules
 */

import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { ToastContainer } from './components/Toast';
import { useToast } from './hooks/useToast';
import Documentation from './components/Documentation';
import ModelDetails from './components/ModelDetails';
import ModelsGallery from './pages/ModelsGallery';
import { API_CONFIG } from './config/api';

const App = () => {
  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState(null);
  const [formData, setFormData] = useState({});
  const [retraining, setRetraining] = useState(null);
  const [currentBackend, setCurrentBackend] = useState('sklearn');
  const [availableBackends, setAvailableBackends] = useState(['sklearn', 'pytorch']);
  const [switchingBackend, setSwitchingBackend] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [showModelDetails, setShowModelDetails] = useState(false);
  const [modelDetails, setModelDetails] = useState(null);
  const [zones, setZones] = useState({});
  const [viewMode, setViewMode] = useState('main'); // 'main' or 'gallery'

  // Toast notifications
  const { toasts, toast, removeToast } = useToast();

  // Guards against two poll loops running at once — pollBackendSwitchStatus can be started
  // either by this tab's own switch request, or by the ambient poll below noticing someone
  // else's switch in progress. A ref (not state) since it's just a lock, not something that
  // should trigger a re-render on its own.
  const pollingRef = useRef(false);

  // Fetch modules on mount
  useEffect(() => {
    fetchModules();
    fetchBackendConfig();
    fetchZones();
  }, []);

  // The ML backend (CURRENT_BACKEND on the server) is one shared, process-global value, not
  // per-user — an intentional admin-style toggle, not a per-session preference (see backend/
  // app.py). This ambient poll is what makes that visible in real time: without it, a switch
  // started in one tab/by one user would only become visible to everyone else on their next
  // page load, with no indication of who changed it or that predictions might now be coming
  // from a different backend than they last saw.
  useEffect(() => {
    const ambientPoll = setInterval(() => {
      if (!pollingRef.current) {
        fetchBackendConfig();
      }
    }, 8000);
    return () => clearInterval(ambientPoll);
  }, []);

  // Fetch model details when module changes
  useEffect(() => {
    if (activeModule) {
      fetchModelDetails(activeModule);
    }
  }, [activeModule]);

  const fetchModules = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_CONFIG.ENDPOINTS.MODULES);
      const data = await response.json();
      setModules(data.modules);
      if (data.modules.length > 0) {
        setActiveModule(data.modules[0].id);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackendConfig = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.BACKEND_CONFIG);
      const data = await response.json();
      setCurrentBackend(data.backend || 'sklearn');
      setAvailableBackends(data.available_backends || ['sklearn', 'pytorch']);

      // The switch itself lives server-side (BACKEND_SWITCH_STATUS in app.py) — a page
      // reload only wipes this tab's local `switchingBackend`/poll-loop state, not the actual
      // in-progress switch. Without this, reloading mid-switch shows an idle dropdown even
      // though a switch (possibly started by a different tab or user) is still running.
      if (data.switch_status === 'switching' && data.switch_target) {
        setSwitchingBackend(true);
        pollBackendSwitchStatus(data.switch_target);
      }
    } catch (error) {
      console.error('Error fetching backend config:', error);
      setCurrentBackend('sklearn');
      setAvailableBackends(['sklearn', 'pytorch']);
    }
  };

  const fetchZones = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.ZONES);
      const data = await response.json();
      setZones(data.zones);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const fetchModelDetails = async (moduleId) => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.MODEL_DETAILS(moduleId));
      const data = await response.json();
      setModelDetails(data.model || data);

      // Update modules with metrics from the model-details response
      if (data.metrics && Object.keys(data.metrics).length > 0) {
        setModules(prev => prev.map(m =>
          m.id === moduleId
            ? {...m, metrics: data.metrics}
            : m
        ));
      }
    } catch (error) {
      console.error('Error fetching model details:', error);
    }
  };

  // Re-runs the current prediction after a backend switch completes, same params as before —
  // split out since both the (rare) immediate-done path and the polled-done path need it.
  const rerunPredictionAfterSwitch = async (backend) => {
    if (!predictions || Object.keys(formData).length === 0) return;
    try {
      const predResponse = await fetch(
        API_CONFIG.ENDPOINTS.PREDICT(activeModule),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        }
      );
      if (predResponse.ok) {
        const predData = await predResponse.json();
        setPredictions(predData);
        toast.info(`Predictions updated with ${backend} backend`);
      }
    } catch (err) {
      console.error('Error re-running prediction:', err);
    }
  };

  // The backend now switches on a background thread (real PyTorch/sklearn training is too slow
  // to run inline behind one HTTP request — see backend/app.py's set_backend()), so POSTing
  // here only starts the switch. Poll GET on the same endpoint until switch_status settles.
  // Can be entered two ways: this tab's own handleBackendChange, or the ambient poll noticing
  // someone else's switch already in progress — pollingRef prevents both paths racing.
  const pollBackendSwitchStatus = async (targetBackend) => {
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      await pollBackendSwitchStatusLoop(targetBackend);
    } finally {
      pollingRef.current = false;
    }
  };

  const pollBackendSwitchStatusLoop = async (targetBackend) => {
    const POLL_INTERVAL_MS = 3000;
    const MAX_ATTEMPTS = 200; // ~10 minutes, matching gunicorn's --timeout 600 on the backend

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

      try {
        const response = await fetch(API_CONFIG.ENDPOINTS.BACKEND_CONFIG);
        const data = await response.json();

        if (data.switch_status === 'error') {
          toast.error(`Backend switch to ${targetBackend} failed: ${data.switch_error}`);
          setCurrentBackend(data.backend); // unchanged — the old backend's models were never replaced
          setSwitchingBackend(false);
          return;
        }

        if (data.switch_status === 'idle') {
          setCurrentBackend(data.backend);
          toast.success(`Switched to ${targetBackend.charAt(0).toUpperCase() + targetBackend.slice(1)} backend!`);
          setSwitchingBackend(false);
          await rerunPredictionAfterSwitch(targetBackend);
          return;
        }
        // still 'switching' — keep polling
      } catch (err) {
        // A transient network hiccup shouldn't abort the whole poll loop.
        console.error('Error polling backend switch status:', err);
      }
    }

    toast.error('Backend switch is taking unusually long — check the server logs.');
    setSwitchingBackend(false);
  };

  const handleBackendChange = async (backend) => {
    try {
      setSwitchingBackend(true);
      const response = await fetch(API_CONFIG.ENDPOINTS.SWITCH_BACKEND, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backend })
      });

      const data = await response.json();

      if (!response.ok && response.status !== 409) {
        throw new Error(data.error || 'Failed to switch backend');
      }

      if (data.switch_status === 'idle') {
        // Already on this backend — nothing to wait for.
        setCurrentBackend(data.backend);
        toast.success(data.message || `Already using ${backend} backend`);
        setSwitchingBackend(false);
        return;
      }

      // 'switching' (just started, or a 409 means one was already in flight) — poll for it.
      toast.info(`Switching to ${backend} — this can take a while (real model training).`);
      await pollBackendSwitchStatus(backend);
    } catch (error) {
      console.error('Error switching backend:', error);
      toast.error(`Error switching backend: ${error.message}`);
      setSwitchingBackend(false);
    }
  };

  const handlePredict = async (e) => {
    e.preventDefault();
    if (!activeModule) return;

    try {
      const response = await fetch(
        API_CONFIG.ENDPOINTS.PREDICT(activeModule),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        }
      );

      if (!response.ok) throw new Error('Prediction failed');
      const data = await response.json();
      setPredictions(data);
    } catch (error) {
      console.error('Error making prediction:', error);
      toast.error(`Error making prediction: ${error.message}`);
    }
  };

  const handleRetrain = async () => {
    if (!activeModule) return;

    try {
      setRetraining(activeModule);
      const response = await fetch(
        API_CONFIG.ENDPOINTS.RETRAIN(activeModule),
        { method: 'POST' }
      );

      if (!response.ok) throw new Error('Retraining failed');

      // Poll for status
      let isComplete = false;
      let attempts = 0;

      while (!isComplete && attempts < 60) {
        await new Promise(r => setTimeout(r, 1000));

        const statusResponse = await fetch(
          `${API_CONFIG.BASE_URL}/api/v1/training-status/${activeModule}`
        );
        const status = await statusResponse.json();

        if (status.status === 'ready' || status.status === 'error') {
          isComplete = true;
          setRetraining(null);
          if (status.status === 'error') {
            toast.error(`Retraining error: ${status.error}`);
          } else {
            toast.success('Model retrained successfully!');
            // Fetch updated modules to get new metrics
            fetchModules();
          }
        }
        attempts++;
      }
    } catch (error) {
      console.error('Error retraining:', error);
      setRetraining(null);
      toast.error(`Error: ${error.message}`);
    }
  };

  const currentModule = modules.find(m => m.id === activeModule);

  return (
    <div className="app">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <header className="header">
        <div className="header-content">
          <h1>🤖 ANN Governance Frameworks</h1>
          <p>Intelligent Predictions for Nigerian Governance</p>
        </div>
        <div className="header-controls">
          <button
            className="docs-button"
            onClick={() => setViewMode('gallery')}
            title="View all models information"
          >
            📚 Gallery
          </button>
          <button
            className="docs-button"
            onClick={() => setShowDocumentation(true)}
            title="View project documentation"
          >
            📖 Docs
          </button>
          <div className="header-backend-selector">
            <label htmlFor="backend-select">ML Backend:</label>
            <select
              id="backend-select"
              value={currentBackend}
              onChange={(e) => handleBackendChange(e.target.value)}
              disabled={switchingBackend}
              className="backend-select"
            >
              {availableBackends.map((backend) => (
                <option key={backend} value={backend}>
                  {backend.charAt(0).toUpperCase() + backend.slice(1)}
                  {backend === 'pytorch' ? ' 🔥' : ' ⚙️'}
                </option>
              ))}
            </select>
            {switchingBackend && <span className="switching-indicator">Switching...</span>}
          </div>
        </div>
      </header>

      {viewMode === 'gallery' ? (
        <ModelsGallery />
      ) : (
        <div className="container">
          {/* Sidebar - Module Selection */}
          <aside className="sidebar">
          <div className="sidebar-header">
            <h2>Modules</h2>
            <span className="module-count">{modules.length}</span>
          </div>

          <div className="modules-grid">
            {modules.map((module) => (
              <button
                key={module.id}
                className={`module-card ${activeModule === module.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveModule(module.id);
                  setPredictions(null);
                  setFormData({});
                }}
                style={{ borderLeftColor: module.color }}
              >
                <div className="module-icon">{module.icon}</div>
                <div className="module-name">{module.name}</div>
                <span className={`status-badge status-${module.status}`}>
                  {module.status}
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Loading modules...</p>
            </div>
          ) : currentModule ? (
            <div className="module-view">
              {/* Header */}
              <div className="module-header" style={{ borderLeftColor: currentModule.color }}>
                <div>
                  <h2>{currentModule.icon} {currentModule.name}</h2>
                  <p>{currentModule.description}</p>
                </div>
                <div className="module-stats">
                  <div className="stat">
                    <span className="stat-label">Features</span>
                    <span className="stat-value">{currentModule.features}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Status</span>
                    <span className={`stat-value status-${currentModule.status}`}>
                      {currentModule.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="module-content">
                {/* Left: Input Form */}
                <div className="input-section">
                  <form onSubmit={handlePredict} className="prediction-form">
                    <h3>📊 Input Parameters</h3>

                    <div className="form-inputs">
                      {/* Generic inputs based on module */}
                      {getFormFieldsForModule(currentModule, zones).map((field) => (
                        <div key={field.name} className="form-group">
                          <label>{field.label}</label>
                          {field.type === 'select' ? (
                            <select
                              name={field.name}
                              value={formData[field.name] || ''}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  [field.name]: e.target.value
                                });
                              }}
                            >
                              <option value="">-- Select {field.label} --</option>
                              {field.options && field.options.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={field.type}
                              name={field.name}
                              placeholder={field.placeholder}
                              value={formData[field.name] || ''}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  [field.name]: e.target.value
                                });
                              }}
                              min={field.min}
                              max={field.max}
                              step={field.step}
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary">
                        🔮 Make Prediction
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleRetrain}
                        disabled={retraining === activeModule}
                      >
                        {retraining === activeModule ? (
                          <>⏳ Retraining...</>
                        ) : (
                          <>♻️  Retrain Model</>
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Right: Results */}
                <div className="results-section">
                  {predictions ? (
                    <div className="predictions-card">
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                        <h3>✨ Predictions</h3>
                        <span style={{fontSize: '0.85rem', color: '#64748b', backgroundColor: '#f1f5f9', padding: '0.25rem 0.75rem', borderRadius: '6px'}}>
                          🔧 {predictions.backend || 'unknown'}
                        </span>
                      </div>
                      <div className="predictions-grid">
                        {Object.entries(predictions.predictions).map(([key, value]) => (
                          <div key={key} className="prediction-item">
                            <span className="prediction-label">{formatLabel(key)}</span>
                            <span className="prediction-value">
                              {typeof value === 'number' ? value.toFixed(2) : value}
                            </span>
                          </div>
                        ))}
                      </div>
                      <p className="prediction-timestamp">
                        Generated: {new Date(predictions.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">📭</div>
                      <p>No predictions yet</p>
                      <p className="empty-hint">Fill in the form and click "Make Prediction"</p>
                    </div>
                  )}

                  {/* Metrics Display */}
                  {currentModule.metrics && Object.keys(currentModule.metrics).length > 0 && (
                    <div className="metrics-card">
                      <h3>📈 Model Performance</h3>
                      <div className="metrics-grid">
                        {Object.entries(currentModule.metrics).map(([metricName, metricValue]) => (
                          <div key={metricName} className="metric-item">
                            <span className="metric-label">{metricName.toUpperCase()}</span>
                            <span className="metric-value">
                              {typeof metricValue === 'number' ? metricValue.toFixed(3) : metricValue}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p>No module selected</p>
            </div>
          )}
        </main>
      </div>
      )}

      {/* Gallery Back Button */}
      {viewMode === 'gallery' && (
        <button
          className="gallery-back-button"
          onClick={() => setViewMode('main')}
          title="Back to main view"
        >
          ← Back
        </button>
      )}

      {/* Documentation Modal */}
      {showDocumentation && (
        <Documentation onClose={() => setShowDocumentation(false)} />
      )}

      {/* Model Details Modal */}
      {showModelDetails && modelDetails && (
        <ModelDetails moduleId={activeModule} onClose={() => setShowModelDetails(false)} />
      )}

      <footer className="footer">
        <p>ANN Governance Frameworks © {new Date().getFullYear()} | Powered by Pytorch & scikit-learn</p>
      </footer>
    </div>
  );
};

// Helper function to get form fields for each module
function getFormFieldsForModule(module, zones = {}) {
  // Safety check for undefined module
  if (!module || !module.id) {
    return [];
  }

  // Get zone options from zones object (convert from {zone_name: [...states]} format)
  const zoneOptions = zones && Object.keys(zones).length > 0 ? Object.keys(zones) : [];

  const fieldsByModule = {
    agriculture: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'crop', label: 'Crop Type', type: 'text', placeholder: 'e.g., Maize' },
      { name: 'rainfall_mm', label: 'Annual Rainfall (mm)', type: 'number', min: 400, max: 3500, step: 10 },
      { name: 'temperature_mean', label: 'Mean Temperature (°C)', type: 'number', min: 15, max: 40, step: 0.5 },
      { name: 'soil_ph', label: 'Soil pH', type: 'number', min: 3, max: 10, step: 0.1 },
      { name: 'soil_nitrogen', label: 'Soil Nitrogen (mg/kg)', type: 'number', min: 0, max: 200, step: 5 },
      { name: 'fertilizer_amount_kg_ha', label: 'Fertilizer (kg/ha)', type: 'number', min: 0, max: 800, step: 10 },
      { name: 'mechanization_level', label: 'Mechanization Level (%)', type: 'number', min: 0, max: 100, step: 5 },
    ],
    education: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'school_type', label: 'School Type', type: 'text', placeholder: 'Primary/Secondary/Tertiary' },
      { name: 'student_teacher_ratio', label: 'Student-Teacher Ratio', type: 'number', min: 5, max: 80, step: 1 },
      { name: 'has_internet', label: 'Has Internet (0/1)', type: 'number', min: 0, max: 1, step: 1 },
      { name: 'teacher_qualification_score', label: 'Teacher Qualification (%)', type: 'number', min: 0, max: 100, step: 1 },
      { name: 'avg_family_income', label: 'Avg Family Income (₦)', type: 'number', min: 5000, max: 1000000, step: 10000 },
    ],
    weather: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'season', label: 'Season', type: 'text', placeholder: 'Dry/Rainy/Harmattan' },
      { name: 'temperature', label: 'Current Temperature (°C)', type: 'number', min: 10, max: 40, step: 0.5 },
      { name: 'humidity', label: 'Humidity (%)', type: 'number', min: 20, max: 95, step: 1 },
      { name: 'rainfall', label: 'Current Rainfall (mm)', type: 'number', min: 0, max: 300, step: 5 },
      { name: 'wind_speed', label: 'Wind Speed (km/h)', type: 'number', min: 0, max: 40, step: 1 },
      { name: 'pressure', label: 'Atmospheric Pressure (mb)', type: 'number', min: 990, max: 1035, step: 1 },
    ],
    transportation: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'road_condition', label: 'Road Condition (%)', type: 'number', min: 0, max: 100, step: 5 },
      { name: 'vehicle_count_daily', label: 'Daily Vehicle Count', type: 'number', min: 100, max: 100000, step: 100 },
      { name: 'avg_speed_kmh', label: 'Average Speed (km/h)', type: 'number', min: 10, max: 120, step: 5 },
      { name: 'commute_distance_km', label: 'Commute Distance (km)', type: 'number', min: 1, max: 500, step: 10 },
    ],
    political: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'registered_voters', label: 'Registered Voters', type: 'number', min: 50000, max: 5000000, step: 10000 },
      { name: 'party_popularity', label: 'Party Popularity (%)', type: 'number', min: 0, max: 100, step: 1 },
      { name: 'media_coverage_score', label: 'Media Coverage Score', type: 'number', min: 0, max: 100, step: 1 },
      { name: 'unemployment_rate', label: 'Unemployment Rate (%)', type: 'number', min: 0, max: 50, step: 1 },
    ],
    population: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'population_total', label: 'Total Population', type: 'number', min: 100000, max: 20000000, step: 100000 },
      { name: 'population_growth_rate', label: 'Growth Rate (/100)', type: 'number', min: -0.5, max: 4, step: 0.1 },
      { name: 'literacy_rate', label: 'Literacy Rate (%)', type: 'number', min: 20, max: 100, step: 1 },
      { name: 'poverty_rate', label: 'Poverty Rate (%)', type: 'number', min: 5, max: 95, step: 1 },
    ],
    emergency: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'disaster_type', label: 'Disaster Type', type: 'text', placeholder: 'Flooding/Drought/etc' },
      { name: 'rainfall_anomaly', label: 'Rainfall Anomaly (%)', type: 'number', min: -50, max: 200, step: 10 },
      { name: 'trained_responders_count', label: 'Trained Responders', type: 'number', min: 0, max: 500, step: 10 },
      { name: 'emergency_centers_count', label: 'Emergency Centers', type: 'number', min: 0, max: 50, step: 1 },
    ],
    sports: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'sport', label: 'Sport', type: 'text', placeholder: 'Football/Basketball/etc' },
      { name: 'training_hours_per_week', label: 'Training Hours/Week', type: 'number', min: 0, max: 40, step: 1 },
      { name: 'nutrition_quality_score', label: 'Nutrition Quality (%)', type: 'number', min: 0, max: 100, step: 5 },
      { name: 'coaching_quality_score', label: 'Coaching Quality (%)', type: 'number', min: 0, max: 100, step: 5 },
      { name: 'years_experience', label: 'Years Experience', type: 'number', min: 0, max: 30, step: 1 },
    ],
    immunization: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'disease', label: 'Disease', type: 'text', placeholder: 'Measles/Polio/etc' },
      { name: 'vaccination_coverage', label: 'Vaccination Coverage (%)', type: 'number', min: 10, max: 95, step: 1 },
      { name: 'vaccine_availability_score', label: 'Vaccine Availability (%)', type: 'number', min: 0, max: 100, step: 5 },
      { name: 'health_centers_per_10k', label: 'Health Centers per 10k', type: 'number', min: 0, max: 5, step: 0.1 },
    ],
    security: [
      { name: 'zone', label: 'Geopolitical Zone', type: 'select', options: zoneOptions },
      { name: 'crime_type', label: 'Crime Type', type: 'text', placeholder: 'Robbery/Theft/etc' },
      { name: 'unemployment_rate', label: 'Unemployment Rate (%)', type: 'number', min: 5, max: 60, step: 1 },
      { name: 'police_per_1000_people', label: 'Police per 1000 People', type: 'number', min: 0.5, max: 5, step: 0.1 },
      { name: 'population_density', label: 'Population Density (per km²)', type: 'number', min: 5, max: 5000, step: 10 },
      { name: 'street_lighting_coverage', label: 'Street Lighting (%)', type: 'number', min: 0, max: 100, step: 5 },
    ],
  };

  return fieldsByModule[module.id] || [];
}

function formatLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default App;
